import {jsx, ParamComponent} from 'panel';
import {selx} from 'clsx';
import copy from 'clipboard-copy';
import partition from 'lodash/partition';
import uniqBy from 'lodash/uniqBy';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {ElmRefs, UserSearchTagInputEl} from 'common/widgets/sharing/modal/user-search-tag-input';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {
  DetailedUser,
  DomainGetResponse,
  RoleDescriptor,
  UserInvite,
} from 'common/report/queries/app-api/legacy/org-types';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {capitalize, isValidEmailAddress, isVerifiedDomain, pluralize} from 'common/util/string';
import {events, properties, track} from 'common/report/util/settings-tracking';
import {addOrgUsers, addUsersToOrgProjects} from 'common/report/queries/app-api/legacy/orgs-settings-client';
import {SpinnerEl} from 'common/components/spinner';
import {addToast} from 'common/components/toast-area/toast-manager';
import {reporter} from 'common/report/error-reporting';
import {ToastType} from 'common/components/toast/types';
import {CurrentProject} from 'common/report/model-resources/projects';
import {IronContextRegistry} from 'common/report/context-registry';
import {ProjectMember} from 'apps/settings/src/api/project-types';
import {DisplaySize} from 'common/components/tooltip/types';

import css from './index.module.styl?inline';
import styles from './index.module.styl';

interface Invitee {
  email: string;
  name?: string;
  id: number;
  isOrgMember: boolean;
}

interface State {
  isLoading: boolean;

  searchText: string;
  showPopover: boolean;

  usersToInvite: Invitee[];
  selectedProjectRole: string;

  invalidEmailForInvite: boolean;

  actionInProgress: boolean;
}

interface Params {
  projectRoles: RoleDescriptor[];
  orgDomains: DomainGetResponse[];
  orgMembers: DetailedUser[];
  orgId: number;
  projectId: number;
  projectMembers: ProjectMember[];

  onDone: () => void;
}

export class ProjectInviteModalContent extends ParamComponent<Params, State, IronContextRegistry> {
  get config() {
    return {
      css,
      contexts: [`currentProject` as const],
      useShadowDom: true,
      defaultState: {
        isLoading: true,
        searchText: null,
        showPopover: false,
        usersToInvite: [],
        selectedProjectRole: `Analyst`,
        invalidEmailForInvite: false,
        actionInProgress: false,
      },
      params: {
        projectRoles: Array,
        orgMembers: Array,
        orgDomains: Array,
        orgId: Number,
        projectId: Number,
        projectMembers: Array,
        onDone: Function,
      },
      hooks: {
        postUpdate: (_updates) => {
          if (this.state.isLoading && Object.values(this.params).every((s) => !!s)) {
            this.update({isLoading: false});
          }
        },
      },
      template: () => {
        if (this.state.isLoading) {
          return (
            <div sel={selx(styles.emptyState)}>
              <SpinnerEl />
            </div>
          );
        }

        const {searchText, showPopover} = this.state;
        return (
          <div sel={selx(styles.topSpacer)}>
            <UserSearchTagInputEl
              sel={selx(styles.input)}
              params={{
                tags: this.getTags(),
                selectOptions: [{items: this.projectRoleOption()}],
                selectedInputOption: this.state.selectedProjectRole,
                placeholder: `Enter Email(s)`,
                menuSections: this.getMenuSections(),
                itemMenuKeydownProxy: this.elmRefs.tagInput,
                sectionsFromInputFn: this.addInviteSection,
                picker: {
                  handleInputChange: this.handleInputChange,
                  handleItemClick: this.handleItemClick,
                  handleDropMenuInsert: this.handleDropMenuInsert,
                  handleInputFocus: this.handleInputFocus,
                  handleLevelSelectChange: this.handleLevelSelectChange,
                  handleMpTagInputInsert: this.handleMpTagInputInsert,
                  handlePopoverChange: this.handlePopoverChange,
                  handleWrapperInsert: this.handleWrapperInsert,
                },
                cta: {
                  text: `Invite`,
                  disabled: this.state.usersToInvite.length === 0 || this.state.actionInProgress,
                  ...(this.state.actionInProgress ? {icon: `spinner`} : {}),
                  onClick: this.sendInvitation,
                },
                searchText,
                showPopover,
              }}
            />
            <div sel={selx(styles.divider)} />
            <div sel={selx(styles.footer)}>
              <ButtonEl
                attrs={{
                  theme: ButtonTheme.Tertiary,
                  icon: `link`,
                  tooltip: {
                    text: `Copy Invite Link`,
                  },
                }}
                on={{
                  click: this.copyInviteUrl,
                }}
              >
                Invite Link
              </ButtonEl>
              <ButtonEl
                attrs={{theme: ButtonTheme.Tertiary, icon: `brand-settings`}}
                on={{
                  click: this.openProjectSettings,
                }}
              >
                Project Settings
              </ButtonEl>
            </div>
          </div>
        );
      },
    };
  }

  addInviteSection = (input) => {
    if (input) {
      let tooltip;

      if (!isVerifiedDomain(input, this.params.orgDomains)) {
        tooltip = {
          text: `This email is not a part of your organization`,
          'always-show': true,
        };
      } else if (this.state.invalidEmailForInvite) {
        tooltip = {
          text: `Invalid Email`,
          'always-show': true,
        };
      }

      return [
        {
          items: [
            {
              added: true,
              subLabel: `Invite to organization via email`,
              label: input,
              value: input,
              icon: `plus`,
              tooltip,
            },
          ],
        },
      ];
    }

    return null;
  };

  getTags = () => {
    return this.state.usersToInvite.map((user) => {
      return {
        label: user.name ?? user.email,
        value: user.email,
        attrs: {'tag-name': user.name ?? user.email, removable: true},
      };
    });
  };

  projectRoleOption(): Section[] {
    return uniqBy(this.params.projectRoles, (role) => role.name).map((pr: any) => ({
      ...pr,
      label: capitalize(pr.name),
      subLabel: pr.description,
      wrapSubLabel: true,
    }));
  }

  getMenuSections(): Section[] {
    return [{label: `Users`, items: this.getOrgUserItems()}];
  }

  isProjectMember(invitee: any): boolean {
    return this.params.projectMembers.some((member) => member.email === invitee.email);
  }

  sendInvitation = async () => {
    const [existingMembers, invitee] = partition(this.state.usersToInvite, (user) => user.isOrgMember);

    // Update grant for existing members
    const userCount = new Set(existingMembers.map((u) => u.email)).size;

    this.update({actionInProgress: true});

    const projectDescriptor = {
      id: this.params.projectId,
      users: existingMembers.map((member) => {
        return {
          email: member.email,
          role: this.state.selectedProjectRole.toLocaleLowerCase(),
        };
      }),
    };

    if (userCount > 0) {
      try {
        await addUsersToOrgProjects(this.params.orgId, [projectDescriptor]);

        const userText = pluralize(`user`, userCount);
        addToast({
          message: `Access updated for ${userCount} existing ${userText}.`,
          type: ToastType.SUCCESS,
        });
      } catch (err) {
        reporter.error(err, `Failed updating existing user project grants`);
      }
    }

    // sanitize user data and transform project ids/role into project objects
    const userInvites: UserInvite[] = invitee.map(({email, name}) => ({
      email,
      name,
      projects: [{id: this.params.projectId, role: this.state.selectedProjectRole.toLocaleLowerCase()}],
    }));

    let isSuccessful;
    let successfulInvitations = [];
    let failedInvitations = [];

    try {
      const rawResponse = await addOrgUsers(this.params.orgId, userInvites);
      isSuccessful = rawResponse.every((response) => typeof response.error === `undefined`);

      if (isSuccessful) {
        successfulInvitations = rawResponse;
      } else {
        failedInvitations = rawResponse.filter((response) => !response.invited);
      }
    } catch (error) {
      isSuccessful = false;
      let errorMsg;

      try {
        errorMsg = JSON.parse(error.message);
      } catch (jsonError) {
        errorMsg = [];
      }

      errorMsg.forEach((message) => {
        const response = message.split(`: `);
        if (response.length === 2) {
          const failureMessage = `Error inviting users: ${response[0]}`;
          const emails = response[1].split(`\n`);
          failedInvitations = failedInvitations.concat(
            emails.map((email) => ({
              email,
              error: failureMessage,
            })),
          );
        }
      });
    }

    if (failedInvitations.length > 0) {
      const firstError = failedInvitations[0]?.error;

      addToast({
        message: firstError ? `Error inviting users: ${firstError}` : `Something went wrong. Please try again.`,
        type: ToastType.ERROR,
      });
    }

    // success
    if (isSuccessful) {
      const numberOfEmails = successfulInvitations.filter((inv) => inv.invited).length;
      const invitationsText = pluralize(`invitation`, numberOfEmails);
      const hasText = pluralize(`has`, numberOfEmails, `have`);
      const invitationSentence =
        numberOfEmails > 0 ? `${numberOfEmails} email ${invitationsText} ${hasText} been sent. ` : ``;

      const numberAdded = successfulInvitations.filter((inv) => inv.added).length;
      const userText = pluralize(`user`, numberAdded);
      const addHasText = pluralize(`has`, numberAdded, `have`);
      const addedSentence =
        numberAdded > 0 ? `${numberAdded} ${userText} ${addHasText} been added to the organization.` : ``;

      if (invitationSentence || addedSentence) {
        addToast({
          message: `${invitationSentence}${addedSentence}`,
          type: ToastType.SUCCESS,
        });
      }

      track(events.inviteUser, {
        [properties.numUsers]: numberOfEmails,
        [properties.type]: `Single`,
        [properties.tab]: `Users`,
        isV2: true,
      });

      this.params.onDone();
    }

    this.update({actionInProgress: false});
  };

  orgUserItemsTooltip(member: DetailedUser) {
    if (this.isProjectMember(member)) {
      return {
        isDisabled: true,
        tooltip: {
          text: `This person already has access to this project. To edit their permissions, go to Project Settings.`,
          size: DisplaySize.Small,
        },
      };
    }
    if (member.status === `Invited`) {
      return {
        isDisabled: true,
        tooltip: {
          text: `This person has already been invited to this project.`,
          size: DisplaySize.Small,
        },
      };
    }
    return {isDisabled: false, tooltip: null};
  }

  getOrgUserItems(): SectionItem[] {
    return this.params.orgMembers.map((member) => {
      return {
        label: `${member.email}`,
        value: member,
        ...this.orgUserItemsTooltip(member),
      };
    });
  }

  handleInputChange = ({detail}) => {
    this.update({showPopover: true, invalidEmailForInvite: false});
    if (detail.action === `text`) {
      this.update({searchText: detail.value});
    } else if (detail.action === `removeTag`) {
      this.update({usersToInvite: this.state.usersToInvite.filter((user) => user.email !== detail.tag.value)});
    }
  };

  handleItemClick = (ev) => {
    const value = ev.detail.item.value;

    if (value.id && !this.state.usersToInvite.some((user) => user.id === value.id)) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const {id, email, first_name, last_name} = this.params.orgMembers.find((orgMember) => orgMember.id === value.id);
      this.update({
        searchText: ``,
        usersToInvite: [
          ...this.state.usersToInvite,
          {email, id, isOrgMember: true, name: `${first_name} ${last_name}`},
        ],
        invalidEmailForInvite: false,
      });
    } else {
      if (isValidEmailAddress(value) && isVerifiedDomain(value, this.params.orgDomains)) {
        this.update({
          searchText: ``,
          usersToInvite: [...this.state.usersToInvite, {email: value, id: value, isOrgMember: false}],
          invalidEmailForInvite: false,
        });
      } else if (value.length > 2) {
        this.update({invalidEmailForInvite: true});
      }
    }

    this.elmRefs[ElmRefs.TagInput].focus();
  };

  handleDropMenuInsert = ({elm}) => this.setElmRef(`dropMenu`, elm as HTMLElement);
  handleWrapperInsert = ({elm}) => this.setElmRef(`tagInputWrapper`, elm as HTMLElement);

  handleInputFocus = () => {
    this.update({showPopover: true});
  };

  handleLevelSelectChange = ({detail}) => {
    const {item} = detail;
    this.update({selectedProjectRole: item.label});
  };

  handleMpTagInputInsert = ({elm}) => {
    this.setElmRef(ElmRefs.TagInput, elm);
    elm.focus();
  };

  handlePopoverChange = ({detail}) => {
    if (detail.state === `closed`) {
      this.update({showPopover: false});
    }
  };

  private getCurrentProject(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  projectName = () => {
    const project = this.getCurrentProject();
    return project && project.name;
  };

  openProjectSettings = () => {
    track(`Open Project Settings`, {isV2: true});
    window.location.href =
      window.location.origin + `/project/${this.params.projectId}/app/settings#project/${this.params.projectId}/users`;
    this.params.onDone();
  };

  copyInviteUrl = async () => {
    track(`Copy Invite Url`, {
      isV2: true,
    });

    try {
      await copy(window.location.origin + `/request_access/?next=/project/${this.params.projectId}/app/boards`);
      addToast({
        message: `URL copied!`,
      });
    } catch (err) {
      reporter.error(err, `Could not copy URL`);
      addToast({
        message: `Could not copy the URL`,
        type: ToastType.ERROR,
      });
    }
  };

  elmRefs: Record<ElmRefs, HTMLElement | null> = {
    [ElmRefs.TagInput]: null,
    [ElmRefs.TagInputWrapper]: null,
    [ElmRefs.DropMenu]: null,
  };

  setElmRef(key: string, elm: HTMLElement) {
    this.elmRefs[key] = elm;
  }
}

export const ProjectInviteModalContentEl = defineAndMakeJsxFactory(
  `mp-project-invite-modal-content`,
  ProjectInviteModalContent,
);
